import { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { Plus, Trash2, Save, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNELS = ['Flipkart', 'Amazon'];

export default function AdsSuperset({ user }) {
  const [channel, setChannel] = useState('Flipkart');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // {type:'row'|'col', index, x, y}
  const fileInputRef = useRef(null);

  useEffect(() => { fetchData(channel); }, [channel]); // eslint-disable-line

  // Hide context menu on click elsewhere
  useEffect(() => {
    const close = () => setContextMenu(null);
    if (contextMenu) {
      window.addEventListener('click', close);
      return () => window.removeEventListener('click', close);
    }
  }, [contextMenu]);

  const fetchData = async (ch) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/ads-superset/grid?channel=${encodeURIComponent(ch)}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setHeaders(d.headers || []);
        setRows(d.rows || []);
        setDirty(false);
      }
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/ads-superset/grid`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, headers, rows })
      });
      if (res.ok) {
        setDirty(false);
        setSavedAt(new Date());
      }
    } finally { setSaving(false); }
  };

  const reset = () => fetchData(channel);

  // Cell edit
  const updateCell = (rowIdx, colIdx, value) => {
    const next = rows.map((r, i) => i === rowIdx ? r.map((c, j) => j === colIdx ? value : c) : r);
    setRows(next); setDirty(true);
  };
  const updateHeader = (colIdx, value) => {
    const next = headers.map((h, i) => i === colIdx ? value : h);
    setHeaders(next); setDirty(true);
  };

  // Row ops
  const addRow = (atIdx) => {
    const newRow = headers.map(() => '');
    const idx = atIdx === undefined ? rows.length : atIdx;
    setRows([...rows.slice(0, idx), newRow, ...rows.slice(idx)]);
    setDirty(true);
  };
  const deleteRow = (idx) => {
    if (!window.confirm('Delete this row?')) return;
    setRows(rows.filter((_, i) => i !== idx)); setDirty(true);
  };
  const duplicateRow = (idx) => {
    const copy = [...rows[idx]];
    setRows([...rows.slice(0, idx + 1), copy, ...rows.slice(idx + 1)]);
    setDirty(true);
  };

  // Column ops
  const addColumn = (atIdx) => {
    const name = window.prompt('Column name:', `Column ${headers.length + 1}`);
    if (name === null) return;
    const idx = atIdx === undefined ? headers.length : atIdx;
    setHeaders([...headers.slice(0, idx), name, ...headers.slice(idx)]);
    setRows(rows.map(r => [...r.slice(0, idx), '', ...r.slice(idx)]));
    setDirty(true);
  };
  const deleteColumn = (idx) => {
    if (!window.confirm(`Delete column "${headers[idx]}" and all its values?`)) return;
    setHeaders(headers.filter((_, i) => i !== idx));
    setRows(rows.map(r => r.filter((_, i) => i !== idx)));
    setDirty(true);
  };

  // Filtering — visible only, doesn't mutate data
  const [search, setSearch] = useState('');
  const matchesSearch = (r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return r.some(c => String(c).toLowerCase().includes(q));
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="ads-superset" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0F0F0F]">Ads Superset</h1>
              <p className="text-sm text-gray-500 mt-0.5">Editable property catalogue. Click any cell to edit.</p>
            </div>
            <div className="flex items-center gap-2">
              {dirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
              {!dirty && savedAt && <span className="text-xs text-gray-500">Saved {savedAt.toLocaleTimeString()}</span>}
              <button
                data-testid="superset-reset-btn"
                onClick={reset}
                disabled={!dirty || saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-40">
                <RotateCcw size={14} /> Reset
              </button>
              <button
                data-testid="superset-save-btn"
                onClick={save}
                disabled={!dirty || saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#37b6ad] text-white rounded-lg hover:bg-[#2a9d95] disabled:opacity-50">
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6" data-testid="ads-superset-page">

      {/* Channel switcher + Search */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex bg-gray-100 rounded-lg p-1">
          {CHANNELS.map(c => (
            <button
              key={c}
              data-testid={`superset-channel-${c.toLowerCase()}`}
              onClick={() => {
                if (dirty && !window.confirm('You have unsaved changes. Switch channel anyway?')) return;
                setChannel(c);
              }}
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition ${channel === c ? 'bg-white text-[#0F0F0F] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              {c}
            </button>
          ))}
        </div>
        <input
          data-testid="superset-search"
          type="text"
          placeholder="Search any cell…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 max-w-xs px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#37b6ad]" />
        <span className="text-xs text-gray-500">
          {rows.filter(matchesSearch).length} / {rows.length} rows · {headers.length} cols
        </span>
      </div>

      {/* Spreadsheet */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-auto bg-white shadow-sm" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          <table className="text-sm" data-testid="superset-table">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="w-10 px-2 py-2 text-xs text-gray-400 border-b border-gray-200 sticky left-0 bg-gray-50 z-20">#</th>
                {headers.map((h, ci) => (
                  <th key={ci}
                      className="px-2 py-2 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-200 group relative min-w-[160px]"
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ type: 'col', index: ci, x: e.clientX, y: e.clientY }); }}>
                    <input
                      data-testid={`superset-header-${ci}`}
                      type="text"
                      value={h}
                      onChange={e => updateHeader(ci, e.target.value)}
                      className="w-full bg-transparent uppercase tracking-wide outline-none focus:bg-white focus:px-1 focus:rounded" />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 flex gap-0.5 transition">
                      <button
                        data-testid={`superset-add-col-after-${ci}`}
                        onClick={() => addColumn(ci + 1)}
                        title="Insert column after"
                        className="p-1 hover:bg-gray-200 rounded">
                        <Plus size={12} />
                      </button>
                      <button
                        data-testid={`superset-del-col-${ci}`}
                        onClick={() => deleteColumn(ci)}
                        title="Delete column"
                        className="p-1 hover:bg-red-100 text-red-500 rounded">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </th>
                ))}
                <th className="px-2 py-2 border-b border-gray-200 sticky right-0 bg-gray-50">
                  <button
                    data-testid="superset-add-col-end"
                    onClick={() => addColumn()}
                    title="Add column"
                    className="p-1 hover:bg-gray-200 rounded text-gray-500">
                    <Plus size={14} />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                if (!matchesSearch(row)) return null;
                return (
                  <tr key={ri} className="group hover:bg-gray-50/50 border-b border-gray-100">
                    <td
                      className="px-2 py-1 text-xs text-gray-400 border-r border-gray-200 sticky left-0 bg-white group-hover:bg-gray-50/50 cursor-context-menu select-none"
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ type: 'row', index: ri, x: e.clientX, y: e.clientY }); }}
                      title="Right-click for options">
                      {ri + 1}
                    </td>
                    {row.map((cell, ci) => (
                      <td key={ci} className="border-r border-gray-100 align-top">
                        <textarea
                          data-testid={`superset-cell-${ri}-${ci}`}
                          value={cell || ''}
                          onChange={e => updateCell(ri, ci, e.target.value)}
                          rows={1}
                          className="w-full px-2 py-1.5 text-sm bg-transparent outline-none resize-none focus:bg-white focus:ring-2 focus:ring-[#37b6ad]/40 rounded"
                          style={{ minHeight: '32px', minWidth: '160px' }}
                          onInput={(e) => {
                            // auto-grow
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }} />
                      </td>
                    ))}
                    <td className="sticky right-0 bg-white group-hover:bg-gray-50/50 px-2 py-1">
                      <button
                        data-testid={`superset-row-menu-${ri}`}
                        onClick={(e) => { e.stopPropagation(); setContextMenu({ type: 'row', index: ri, x: e.clientX, y: e.clientY }); }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded">
                        <ChevronDown size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* Add-row footer */}
              <tr>
                <td colSpan={headers.length + 2} className="p-0">
                  <button
                    data-testid="superset-add-row"
                    onClick={() => addRow()}
                    className="w-full py-2 flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-[#37b6ad] border-t border-gray-200">
                    <Plus size={12} /> Add Row
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          data-testid="superset-context-menu"
          onClick={(e) => e.stopPropagation()}
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px]"
          style={{ top: Math.min(contextMenu.y, window.innerHeight - 220), left: Math.min(contextMenu.x, window.innerWidth - 200) }}>
          {contextMenu.type === 'row' ? (
            <>
              <button onClick={() => { addRow(contextMenu.index); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"><ChevronRight size={12} /> Insert row above</button>
              <button onClick={() => { addRow(contextMenu.index + 1); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"><ChevronRight size={12} /> Insert row below</button>
              <button onClick={() => { duplicateRow(contextMenu.index); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"><ChevronRight size={12} /> Duplicate row</button>
              <div className="border-t border-gray-100 my-1" />
              <button onClick={() => { deleteRow(contextMenu.index); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"><Trash2 size={12} /> Delete row</button>
            </>
          ) : (
            <>
              <button onClick={() => { addColumn(contextMenu.index); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"><ChevronRight size={12} /> Insert column before</button>
              <button onClick={() => { addColumn(contextMenu.index + 1); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"><ChevronRight size={12} /> Insert column after</button>
              <div className="border-t border-gray-100 my-1" />
              <button onClick={() => { deleteColumn(contextMenu.index); setContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"><Trash2 size={12} /> Delete column</button>
            </>
          )}
        </div>
      )}
        </main>
      </div>
    </div>
  );
}
